import { useState, useEffect } from 'react';
import useIdleTimer from '../hooks/useIdleTimer';

export default function useAppSession({ user, setUser, navigate }) {
  const [sessionConfig, setSessionConfig] = useState(null);

  // Fetch session configuration on mount
  useEffect(() => {
    const fetchSessionConfig = async () => {
      try {
        const response = await fetch('/portal/api/auth/session-config', {
          credentials: 'include'
        });
        const config = await response.json();
        if (config.success) {
          setSessionConfig(config);
        } else {
          throw new Error('Failed to fetch session config');
        }
      } catch (error) {
        console.warn('Failed to fetch session config, using defaults:', error);
        // Fallback to default values
        setSessionConfig({
          success: true,
          idleTimeoutSeconds: 1800, // 30 minutes
          idleWarningSeconds: 60,   // 1 minute
          heartbeatMs: 60000        // 1 minute
        });
      }
    };

    fetchSessionConfig();
  }, []);

  // Handle auto-logout (idle timeout)
  const handleAutoLogout = async () => {
    // For idle timeout, set session_expired flag (not logged_out)
    // This shows "Your session has expired" message on login page
    localStorage.setItem('faithinsight_session_expired', 'true');
    localStorage.removeItem('faithinsight_logged_out');

    // Clear user data locally without calling API (user was already inactive)
    localStorage.removeItem('faithinsight_user');
    localStorage.removeItem('faithinsight_token');
    localStorage.removeItem('faithinsight_refresh_token');

    // Update AuthContext state
    setUser(null);

    // Navigate to login
    navigate('/login');
  };

  // NOTE: The access-token expiration warning modal was intentionally removed.
  // Access tokens are refreshed silently by services/api.js on the next request
  // (see the 401 handler), so an expiring token never needs to interrupt the user.
  // The only session prompt users see is the inactivity ("stay logged in") warning,
  // keeping a single, understandable session concept.

  // Check for app updates on user activity (every API call)
  useEffect(() => {
    if (!user) return;

    // Check for updates when user becomes active
    const checkOnActivity = () => {
      // Import versionService dynamically to avoid circular dependencies
      import('../services/versionService').then(({ default: versionService }) => {
        if (versionService.shouldCheck()) {
          versionService.forceCheck();
        }
      });
    };

    // Check on mouse/keyboard activity (debounced)
    let activityTimeout;
    const handleActivity = () => {
      clearTimeout(activityTimeout);
      activityTimeout = setTimeout(checkOnActivity, 5000); // Check 5 seconds after activity
    };

    window.addEventListener('mousedown', handleActivity);
    window.addEventListener('keydown', handleActivity);
    window.addEventListener('touchstart', handleActivity);

    return () => {
      window.removeEventListener('mousedown', handleActivity);
      window.removeEventListener('keydown', handleActivity);
      window.removeEventListener('touchstart', handleActivity);
      clearTimeout(activityTimeout);
    };
  }, [user]);

  // Initialize idle timer with session config only when user is logged in and config is available
  const idleTimer = useIdleTimer({
    idleTimeoutMs: sessionConfig ? sessionConfig.idleTimeoutSeconds * 1000 : 1800000, // Convert seconds to ms
    warningSeconds: sessionConfig ? sessionConfig.idleWarningSeconds : 60,
    heartbeatMs: sessionConfig ? sessionConfig.heartbeatMs : 60000,
    enabled: !!user && !!sessionConfig, // Only enable when user is logged in and config is available
    onShowWarning: () => {
      // Optional: Add any additional logic when warning is shown
    },
    onHideWarning: () => {
      // Optional: Add any additional logic when warning is hidden
    },
    onAutoLogout: handleAutoLogout
  });

  return { sessionConfig, idleTimer, handleAutoLogout };
}
